import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { heroSubtitleClass, heroTitleClass, motionSafe } from '../constants/heroTypography';
import heroBg from '../assets/img/banner_image.png';
import mapThumb from '../assets/img/about_img.jpg';
import bottomImage from '../assets/img/banner_image.png';

/**
 * Netlify Forms: Site settings → Forms → Form notifications → Email → rayanstarbh@gmail.com
 * Optional: set REACT_APP_WEB3FORMS_ACCESS_KEY in Netlify env (https://web3forms.com) if Netlify Forms fails.
 */

function netlifyFormSucceeded(res) {
  if (res.ok) return true;
  // Netlify often responds with 302/303 on success; fetch with redirect:manual leaves ok=false
  return res.status === 301 || res.status === 302 || res.status === 303 || res.status === 307 || res.status === 308;
}

async function postNetlifyForm(body) {
  const opts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
    redirect: 'manual',
  };
  const paths = ['/', '/index.html'];
  for (const path of paths) {
    const res = await fetch(path, opts);
    if (netlifyFormSucceeded(res)) return true;
  }
  return false;
}

async function postWeb3Forms(payload) {
  const key = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
  if (!key) return false;
  try {
    const res = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ access_key: key, ...payload }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.success === true;
  } catch {
    return false;
  }
}

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSending(true);

    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = fd.get('name')?.toString().trim() ?? '';
    const email = fd.get('email')?.toString().trim() ?? '';
    const phone = fd.get('phone')?.toString().trim() ?? '';
    const message = fd.get('message')?.toString().trim() ?? '';

    const isLocal =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocal) {
      setSending(false);
      setSent(true);
      return;
    }

    try {
      const web3Payload = {
        subject: 'RAYAN STAR website — Contact form',
        name,
        email,
        phone,
        message,
        from_name: name,
      };

      const body = new URLSearchParams(fd).toString();
      let ok = await postNetlifyForm(body);
      if (!ok && process.env.REACT_APP_WEB3FORMS_ACCESS_KEY) {
        ok = await postWeb3Forms(web3Payload);
      }
      if (!ok) {
        throw new Error('Request failed');
      }
      setSent(true);
      form.reset();
    } catch {
      setError('Something went wrong. Please email rayanstarbh@gmail.com directly.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-navy-950">
      <PageHero
        title="Contact Us"
        subtitle="Let’s Connect. We are here to assist you"
        backgroundImage={heroBg}
        titleClassName={heroTitleClass}
        subtitleClassName={heroSubtitleClass(motionSafe)}
      />

      {/* Get in touch section */}
      <section className="bg-navy-900/80 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-white sm:text-4xl">Get in Touch</h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-white/75 sm:text-base">
              Reach out to RAYAN STAR Consultancy via phone, email, or visit ready to help you succeed.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="grid gap-0 lg:grid-cols-12">
              {/* Left column */}
              <aside className="border-b border-slate-200 bg-white/70 p-6 sm:p-8 lg:col-span-4 lg:border-b-0 lg:border-r">
                <div className="space-y-7">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gold/20">
                      <svg className="h-5 w-5 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                        />
                      </svg>
                    </span>
                    <div>
                      <h3 className="font-sans text-sm font-semibold text-navy">Call Us</h3>
                      <p className="mt-1 space-y-1 text-sm text-navy/70">
                        <a className="block font-semibold text-navy hover:text-gold" href="tel:+97333671305">
                          +973 3367 1305
                        </a>

                        
                        <a className="block font-semibold text-navy hover:text-gold" href="tel:+97333671307">
                          +973 3367 1307
                        </a>
                        <a className="block font-semibold text-navy hover:text-gold" href="tel:+97377971307">
                          +973 7797 1307
                        </a>
                        <a className="block font-semibold text-navy hover:text-gold" href="tel:+97333671307">
                        +966 55993 6507
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gold/20">
                      <svg className="h-5 w-5 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15A2.25 2.25 0 0 1 2.25 17.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15A2.25 2.25 0 0 0 2.25 6.75m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91A2.25 2.25 0 0 1 2.25 6.993V6.75"
                        />
                      </svg>
                    </span>
                    <div>
                      <h3 className="font-sans text-sm font-semibold text-navy">Email Us</h3>
                      <p className="mt-1 space-y-1 text-sm text-navy/70">
                        <a className="block font-semibold text-navy hover:text-gold" href="mailto:info@rayanstar.me">
                          info@rayanstar.me
                        </a>
                        <a className="block font-semibold text-navy hover:text-gold" href="mailto:rayanstarbh@gmail.com">
                          rayanstarbh@gmail.com
                        </a>
                      </p>
                      
                      <p className="mt-1 text-xs text-navy/60">Response within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gold/20">
                      <svg className="h-5 w-5 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                      </svg>
                    </span>
                    <div>
                      <h3 className="font-sans text-sm font-semibold text-navy">Visit Us</h3>
                      <p className="mt-1 text-sm leading-relaxed text-navy/70">
                        Office 44, Building 2415
                        <br />
                        Road 2831, Block 428
                        <br />
                        AL-Seef, Mashtan Building
                        <br />
                        Manama, Kingdom of Bahrain
                      </p>
                      <p className="mt-1 text-xs text-navy/60">Friday – Saturday · Emergency support</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gold/20">
                      <svg className="h-5 w-5 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                    </span>
                    <h3 className="font-sans text-sm font-semibold text-gold">Business Hours</h3>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="flex gap-3">
                      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 3v2.25m6.364.886-1.591 1.591M21 12h-2.25m-.886 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                          />
                        </svg>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-navy">Sunday – Thursday</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-navy/70">09:00 –18:00</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21.752 15.002A9.718 9.718 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
                          />
                        </svg>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-navy">Friday – Saturday</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-navy/70">Emergency support</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <img src={mapThumb} alt="Map preview" className="h-40 w-full object-cover" />
                  <div className="flex items-center justify-between px-3 py-2 text-xs text-navy/70">
                    <span>Manama, Kingdom of Bahrain</span>
                    <a
                      className="font-semibold text-gold hover:text-gold-dark"
                      href="https://www.google.com/maps?q=Seef%2C%20Bahrain"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View larger map
                    </a>
                  </div>
                </div>
              </aside>

              {/* Right column */}
              <div className="p-6 sm:p-8 lg:col-span-8">
                {sent ? (
                  <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-4 text-emerald-800">
                    Thank you—your message has been sent. We&apos;ll get back to you soon.
                  </p>
                ) : (
                  <form
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden" aria-hidden="true">
                      <label>
                        Do not fill: <input name="bot-field" tabIndex={-1} autoComplete="off" />
                      </label>
                    </p>
                    <div className="grid gap-4">
                      <div>
                        <label htmlFor="name" className="flex items-center gap-2 text-sm font-semibold text-navy/80">
                          Full Name
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          autoComplete="name"
                          placeholder="Full Name"
                          className="mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-navy shadow-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/25"
                        />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="email" className="text-sm font-semibold text-navy/80">
                            Email Address
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            placeholder="Email Address"
                            className="mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-navy shadow-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/25"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="text-sm font-semibold text-navy/80">
                            Phone Number
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="+973 3367 1305"
                            className="mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-navy shadow-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/25"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="message" className="text-sm font-semibold text-navy/80">
                          Message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          rows={5}
                          required
                          className="mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-navy shadow-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/25"
                        />
                      </div>
                    </div>

                    {error ? (
                      <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                        {error}
                      </p>
                    ) : null}

                    <div className="flex items-start gap-2 text-xs text-navy/70">
                      <input
                        id="contact-privacy-agree"
                        type="checkbox"
                        name="privacy"
                        value="yes"
                        required
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-gold focus:ring-gold/25"
                      />
                      <div>
                        <label htmlFor="contact-privacy-agree" className="cursor-pointer">
                          I agree to the{' '}
                        </label>
                        <Link
                          to="/privacy"
                          className="font-semibold text-gold hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </Link>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="submit"
                        disabled={sending}
                        className="inline-flex items-center justify-center rounded-md bg-gold px-8 py-3 text-sm font-semibold text-navy shadow-sm transition hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {sending ? 'Sending…' : 'Send Message'}
                      </button>

                      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-600 sm:w-[260px]">
                        <div className="flex items-center gap-2">
                          <div className="h-5 w-5 rounded border border-slate-300" aria-hidden />
                          <span>I&apos;m not a robot</span>
                        </div>
                        <span className="text-xs text-slate-400">reCAPTCHA</span>
                      </div>
                    </div>
                  </form>
                )}

                <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <img src={bottomImage} alt="Manama skyline" className="h-48 w-full object-cover sm:h-56" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
